import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { X, ArrowRight, Megaphone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

/**
 * HomeBannerStrip — fetches active homepage_banner ads and renders them as a
 * dismissible top strip on the public home page. Auto-rotates every 6s if
 * multiple banners are live. Renders nothing when there are zero active ads.
 */
export default function HomeBannerStrip() {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const { flags } = useSettings();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ads/active", { params: { ad_type: "homepage_banner" } });
        const items = data.items || [];
        // If founder-club is hidden via admin toggle, drop any banner that links to it
        const filtered = flags.founder_club_visible
          ? items
          : items.filter((b) => !(b.target_url || "").includes("/founder-club"));
        setBanners(filtered);
      } catch { /* silent */ }
    })();
  }, [flags.founder_club_visible]);

  // Session-dismiss so the same visitor isn't nagged on every page-view
  useEffect(() => {
    if (sessionStorage.getItem("ehi_home_banner_dismissed") === "1") setDismissed(true);
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (dismissed || banners.length === 0) return null;

  const b = banners[idx];
  const hasImage = !!b.image_url;
  const target = b.target_url || "#";
  const isExternal = target.startsWith("http");

  const inner = (
    <div className="flex items-center gap-3 min-w-0">
      {hasImage ? (
        <img
          src={b.image_url}
          alt=""
          className="w-8 h-8 object-cover rounded shrink-0 hidden sm:block"
          loading="lazy"
        />
      ) : (
        <Megaphone className="w-4 h-4 text-ehi-accent shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <span className="text-[10px] uppercase tracking-widest font-bold text-ehi-accent mr-2">
          Sponsored
        </span>
        <span className="text-sm font-semibold text-white truncate">{b.title}</span>
        {b.description && (
          <span className="hidden md:inline text-xs text-slate-300 ml-2 truncate">
            · {b.description}
          </span>
        )}
      </div>
      <span className="hidden sm:inline-flex items-center gap-1 text-xs font-bold text-ehi-accent shrink-0">
        Learn more <ArrowRight className="w-3 h-3" />
      </span>
    </div>
  );

  const dismiss = () => {
    try { sessionStorage.setItem("ehi_home_banner_dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 border-b border-purple-500/40 relative"
      data-testid="home-banner-strip"
    >
      <div className="max-w-[1700px] mx-auto px-4 lg:px-8 py-2 pr-10 lg:pr-12">
        {isExternal ? (
          <a href={target} target="_blank" rel="noopener noreferrer" className="block" data-testid={`home-banner-${b.code}`}>
            {inner}
          </a>
        ) : (
          <Link to={target} className="block" data-testid={`home-banner-${b.code}`}>
            {inner}
          </Link>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-full"
        data-testid="home-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      {banners.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-1 pb-0.5">
          {banners.map((_, i) => (
            <span
              key={i}
              className={`w-1 h-1 rounded-full transition-colors ${i === idx ? "bg-ehi-accent" : "bg-slate-600"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
